import React from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  Stack,
  Switch,
  Slider,
  InputAdornment,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// interns scriptrunner - DynamicFormV2 component to render fields based on various types
const DynamicFormV2 = ({ schema, values, onChange, disabled }) => {
  if (!schema) return null;

  const isJsonSchema = !!schema.properties;
  const isArraySchema = Array.isArray(schema.fields);

  if (!isJsonSchema && !isArraySchema) {
    return null;
  }

  const fieldsToRender = isJsonSchema
    ? Object.entries(schema.properties).map(([key, prop]) => ({
        id: key,
        name: prop.title || key,
        type: prop.type,
        format: prop.format,
        enum: prop.enum,
        description: prop.description,
        default: prop.default,
        required: schema.required?.includes(key),
        rows: prop.rows,
        min: prop.minimum || prop.min,
        max: prop.maximum || prop.max,
        step: prop.step,
      }))
    : schema.fields.map((f) => ({
        id: f.id || f.name,
        name: f.label || f.name || f.id,
        type: f.type || "string",
        format: f.format,
        enum: f.enum || f.options,
        description: f.description || f.hoverText,
        default: f.default,
        required: f.required,
        rows: f.rows,
        min: f.min,
        max: f.max,
        step: f.step,
      }));

  const renderField = (field, index) => {
    const {
      id,
      name,
      type,
      format,
      enum: options,
      description,
      required,
      rows,
      min,
      max,
      step,
    } = field;
    const fieldKey = `${id}-${index}`;
    const currentValue = values[id];

    // interns scriptrunner - Dropdown/Select
    if (options && Array.isArray(options)) {
      return (
        <FormControl
          fullWidth
          key={fieldKey}
          sx={{ mb: 3 }}
          disabled={disabled}
        >
          <InputLabel id={`${id}-label`}>{name}</InputLabel>
          <Select
            labelId={`${id}-label`}
            value={currentValue || ""}
            label={name}
            onChange={(e) => onChange(id, e.target.value)}
            required={required}
          >
            {options.map((opt, i) => (
              <MenuItem
                key={typeof opt === "object" ? opt.value || i : opt}
                value={typeof opt === "object" ? opt.value : opt}
              >
                {typeof opt === "object" ? opt.label : opt}
              </MenuItem>
            ))}
          </Select>
          {description && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ ml: 1, mt: 0.5 }}
            >
              {description}
            </Typography>
          )}
        </FormControl>
      );
    }

    // interns scriptrunner - Switch (for booleans that feel like toggles)
    if (type === "switch") {
      return (
        <Box key={fieldKey} sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!currentValue}
                onChange={(e) => onChange(id, e.target.checked)}
                disabled={disabled}
              />
            }
            label={name}
          />
          {description && (
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              sx={{ ml: 4 }}
            >
              {description}
            </Typography>
          )}
        </Box>
      );
    }

    // interns scriptrunner - Checkbox / Boolean
    if (type === "boolean" || type === "checkbox") {
      return (
        <Box key={fieldKey} sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!currentValue}
                onChange={(e) => onChange(id, e.target.checked)}
                disabled={disabled}
              />
            }
            label={name}
          />
          {description && (
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              sx={{ ml: 4 }}
            >
              {description}
            </Typography>
          )}
        </Box>
      );
    }

    // interns scriptrunner - Slider (for ranges)
    if (type === "range" || type === "slider") {
      return (
        <Box key={fieldKey} sx={{ mb: 3, px: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {name}: {currentValue || min || 0}
          </Typography>
          <Slider
            value={typeof currentValue === "number" ? currentValue : min || 0}
            onChange={(_, val) => onChange(id, val)}
            disabled={disabled}
            min={min || 0}
            max={max || 100}
            step={step || 1}
            valueLabelDisplay="auto"
          />
          {description && (
            <Typography variant="caption" color="textSecondary">
              {description}
            </Typography>
          )}
        </Box>
      );
    }

    // interns scriptrunner - File Upload
    if (type === "file") {
      return (
        <Box key={fieldKey} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            {name} {required && "*"}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={disabled}
              size="small"
              sx={{ color: "#1d9f9f", borderColor: "#1d9f9f" }}
            >
              Upload File
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) onChange(id, file);
                }}
              />
            </Button>
            <Typography variant="caption" color="textSecondary">
              {currentValue instanceof File
                ? currentValue.name
                : "No file selected"}
            </Typography>
          </Stack>
          {description && (
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              sx={{ mt: 0.5 }}
            >
              {description}
            </Typography>
          )}
        </Box>
      );
    }

    // interns scriptrunner - Date / Time (Using Native Browser Pickers for Build Stability)
    const isDate = type === "date" || format === "date";
    const isTime = type === "time";
    const isDateTime =
      type === "datetime" ||
      type === "datetime-local" ||
      format === "date-time";

    if (isDate || isTime || isDateTime) {
      let htmlType = "date";
      if (isTime) htmlType = "time";
      if (isDateTime) htmlType = "datetime-local";

      return (
        <TextField
          key={fieldKey}
          fullWidth
          label={name}
          type={htmlType}
          helperText={description}
          value={currentValue || ""}
          onChange={(e) => onChange(id, e.target.value)}
          required={required}
          variant="outlined"
          sx={{ mb: 3 }}
          disabled={disabled}
          InputLabelProps={{ shrink: true }} // interns scriptrunner - required for native date labels
        />
      );
    }

    // interns scriptrunner - Color Picker
    if (type === "color") {
      return (
        <TextField
          key={fieldKey}
          fullWidth
          label={name}
          type="color"
          helperText={description}
          value={currentValue || "#000000"}
          onChange={(e) => onChange(id, e.target.value)}
          required={required}
          variant="outlined"
          sx={{ mb: 3 }}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: currentValue || "#000000",
                    border: "1px solid #ccc",
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      );
    }

    // interns scriptrunner - Default (Text, Password, Number, Textarea)
    let fieldType = "text";
    if (type === "password") fieldType = "password";
    if (type === "number" || type === "integer") fieldType = "number";
    if (type === "email") fieldType = "email";
    if (type === "url") fieldType = "url";

    return (
      <TextField
        key={fieldKey}
        fullWidth
        multiline={type === "textarea" || rows > 1}
        rows={type === "textarea" || rows > 1 ? rows || 4 : 1}
        label={name}
        helperText={description}
        type={fieldType}
        value={currentValue || ""}
        onChange={
          fieldType === "number"
            ? (e) =>
                onChange(
                  id,
                  e.target.value === "" ? "" : Number(e.target.value),
                )
            : (e) => onChange(id, e.target.value)
        }
        required={required}
        variant="outlined"
        sx={{ mb: 3 }}
        placeholder={
          field.default !== undefined ? `Default: ${field.default}` : ""
        }
        disabled={disabled}
        inputProps={{
          min: min,
          max: max,
          step: step,
        }}
      />
    );
  };

  return (
    <Box sx={{ mt: 1 }}>
      {fieldsToRender.map((field, index) => renderField(field, index))}
    </Box>
  );
};

export default DynamicFormV2;
